import type Quill from 'quill/core';
import React, { useEffect, useRef, useState } from 'react';
import closestLine from './closestLine';
import * as styles from './Gutter.module.scss';
import ListReorderGutterButton from './ListReorderGutterButton';
import type QuillEditor from './QuillEditor';
import ReorderGutterButton from './ReorderGutterButton';
import type { Line } from './types';
import PostLayout from '../post/PostLayout';
import useEventListener from '../utils/useEventListener';
import { getFirstNonSoftLine } from './utils/getItemAllLines';

type GutterConfig = {
  reorderingEnabled?: boolean;
  listCollapsingEnabled?: boolean;
};

export interface GutterButtonProps {
  quill: Quill;
  activeBlot: Line;
  buttonTopOffset: number;
  onLockedChange: (locked: boolean) => void;
  config: GutterConfig;
}

const blotMatchers: {
  component: React.FunctionComponent<GutterButtonProps>;
  matcher: (blot: Line, config: GutterConfig) => boolean;
}[] = [
  {
    component: ReorderGutterButton,
    matcher: (blot, config) => {
      if (!config.reorderingEnabled) return false;
      const { blotName } = blot.statics;
      switch (blotName) {
        case 'embed':
        case 'card':
        case 'iframe':
        case 'video':
          return true;
        case 'image':
        case 'attachment':
        case 'file':
          // @ts-expect-error
          return !blot.isUploading();
        default:
          return false;
      }
    },
  },
  {
    component: ListReorderGutterButton,
    matcher: (blot: Line) => blot.statics.blotName === 'list',
  },
];

interface GutterProps {
  quill: QuillEditor;
  hoverableAreaContainer: HTMLElement | null;
  config: GutterConfig;
}

const Gutter = ({ quill, hoverableAreaContainer, config }: GutterProps) => {
  const [activeLine, setActiveLine] = useState<{
    blot: Line;
    component: (typeof blotMatchers)[0]['component'];
  } | null>(null);
  const activeLineLocked = useRef(false);

  const [top, setTop] = useState(0);
  const lastBlot = useRef<Line | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [gutterWidth, setGutterWidth] = useState<number | null>(null);
  const mousePositionYRef = useRef<number | null>(null);

  useEffect(() => {
    if (containerRef.current) {
      setGutterWidth(
        parseInt(getComputedStyle(containerRef.current).width, 10),
      );
    }
  }, []);

  useEventListener(hoverableAreaContainer, 'mouseleave', () => {
    mousePositionYRef.current = null;
    if (activeLineLocked.current) return;
    setActiveLine(null);
    lastBlot.current = null;
  });

  const setStateWithBlot = (blot: Line | null) => {
    const matched =
      blot && blotMatchers.find(({ matcher }) => matcher(blot, config));
    if (matched) {
      const firstLine = getFirstNonSoftLine(blot);
      const container = quill.root.getBoundingClientRect();
      const rect = firstLine.domNode.getBoundingClientRect();
      setActiveLine({ blot: firstLine, component: matched.component });
      setTop(rect.top - container.top);
    } else {
      setActiveLine(null);
    }
  };

  useEventListener(quill.root, 'mouseover', (e) => {
    mousePositionYRef.current = e.clientY;
    if (activeLineLocked.current) return;
    if (e.target === quill.root) return;
    const blot = quill.scroll.find(e.target as Node, true);
    const lineBlot =
      blot && blot.scroll === quill.scroll ? closestLine(blot) : null;
    if (lineBlot === lastBlot.current) return;

    lastBlot.current = lineBlot;
    setStateWithBlot(lineBlot);
  });

  // When mouse is moving inside Gutter, get the line blot that
  // has the same top offset.
  useEventListener(quill.root, 'mousemove', (e) => {
    mousePositionYRef.current = e.clientY;
    if (activeLineLocked.current) return;
    if (
      e.target !== e.currentTarget ||
      (gutterWidth && e.offsetX > gutterWidth)
    )
      return;
    const blot = quill.lineFromYOffset(e.clientY);
    if (blot) {
      setStateWithBlot(blot);
    }
  });

  const onLockedChange = (isLocked: boolean) => {
    if (activeLineLocked.current === isLocked) {
      return;
    }
    activeLineLocked.current = isLocked;
    if (!isLocked) {
      const hoveredLine =
        mousePositionYRef.current != null &&
        quill.lineFromYOffset(mousePositionYRef.current);
      if (hoveredLine) {
        setStateWithBlot(hoveredLine);
      } else {
        lastBlot.current = null;
        setActiveLine(null);
      }
    }
  };

  return (
    <PostLayout.Gutter
      ref={containerRef}
      data-no-print
      className={styles.container}
    >
      {activeLine && (
        <div className={styles.button}>
          <activeLine.component
            quill={quill}
            activeBlot={activeLine.blot}
            config={config}
            buttonTopOffset={top}
            onLockedChange={onLockedChange}
          />
        </div>
      )}
    </PostLayout.Gutter>
  );
};

export default Gutter;
